//
//  SDKTextToken.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//  Copyright © 2021 Supernova. All rights reserved.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
// TODO:fix-sdk-eslint
// eslint-disable-next-line max-classes-per-file
import { SupernovaError } from "../../core/errors/SDKSupernovaError";
import { UnreachableCaseError } from "../../utils/UnreachableCaseError";
import { TokenType } from "../enums/SDKTokenType";
import { Token } from "./SDKToken";
import { v4 as uuidv4 } from "uuid";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: -  Object Definition
export class StringCategoryToken extends Token {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(versionId, baseToken, value, alias, properties, propertyValues) {
        super(baseToken, versionId, properties, propertyValues);
        this.value = value;
        if (alias) {
            this.value.referencedTokenId = alias.id;
        }
    }
    static create(type, versionId, brandId, name, description, value, alias, properties, propertyValues) {
        var _a;
        const baseToken = {
            // @ts-expect-error TS(2322): Type 'undefined' is not assignable to type 'string... Remove this comment to see the full error message
            id: undefined, // Ommited id will create new token
            persistentId: uuidv4(),
            brandId,
            designSystemVersionId: versionId,
            type,
            meta: {
                name,
                description,
            },
            data: {},
            customPropertyOverrides: [],
        };
        if (value !== null && value !== undefined) {
            const tokenValue = {
                text: value.toString(),
                referencedTokenId: null,
            };
            return new this(versionId, baseToken, tokenValue, undefined, properties, propertyValues);
        }
        if (alias) {
            // Aliased value - copy and create raw from reference
            const tokenValue = {
                text: (_a = alias.value) === null || _a === void 0 ? void 0 : _a.text,
                referencedTokenId: alias.id,
            };
            return new this(versionId, baseToken, tokenValue, undefined, properties, propertyValues);
        }
        throw SupernovaError.fromMessage("String Token must be created using value or alias, but none was provided");
    }
    static stringValueFromDefinitionOrReference(definition, referenceResolver, tokenType) {
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        if (referenceResolver.valueHasReference(definition)) {
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            if (!referenceResolver.isBalancedReference(definition)) {
                // Internal syntax of reference corrupted
                throw new Error(
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                `Invalid reference syntax in token value: ${definition}`);
            }
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            if (referenceResolver.valueIsPureReference(definition)) {
                // When color is pure reference, we can immediately resolve it
                const reference = referenceResolver.lookupReferencedToken(
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                definition);
                if (!reference) {
                    // NOTE: This might later be resolved (see Caviar default, fontWeight)
                    return undefined;
                }
                return {
                    referencedTokenId: reference.id,
                    text: reference.value.text,
                };
            }
            // When color is not a pure reference, we must resolve it further before we can resolve it
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            const references = referenceResolver.lookupAllReferencedTokens(definition);
            if (!references) {
                // Still unable to solve the reference, continue looking in some other tokens
                return undefined;
            }
            // Resolved all internal references
            const resolvedValue = referenceResolver.replaceAllReferencedTokens(
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            definition, references);
            return this.stringValueFromDefinition(resolvedValue, tokenType);
        }
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        return this.stringValueFromDefinition(definition, tokenType);
    }
    static stringValueFromDefinition(definition, 
    // TODO:fix-sdk-eslint
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tokenType) {
        return {
            text: definition || "",
            referencedTokenId: null,
        };
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Write
    toWriteObject() {
        const baseData = this.toBaseWriteObject();
        const specificData = baseData;
        specificData.data = StringCategoryToken.valueToWriteObject(this.value);
        return specificData;
    }
    static valueToWriteObject(value) {
        var _a;
        const valueObject = !value.referencedTokenId ? value.text : undefined;
        return {
            aliasTo: (_a = value.referencedTokenId) !== null && _a !== void 0 ? _a : undefined,
            // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
            value: valueObject,
        };
    }
}
export class StringToken extends StringCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.string;
    }
}
export class ProductCopyToken extends StringCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.productCopy;
    }
}
export class FontFamilyToken extends StringCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.fontFamily;
    }
}
export class FontWeightToken extends StringCategoryToken {
    constructor() {
        super(...arguments);
        this.tokenType = TokenType.fontWeight;
    }
}
export const resolveString = (type) => {
    switch (type) {
        case TokenType.string:
            return StringToken;
        case TokenType.productCopy:
            return ProductCopyToken;
        case TokenType.fontFamily:
            return FontFamilyToken;
        case TokenType.fontWeight:
            return FontWeightToken;
        default:
            throw new UnreachableCaseError(type);
    }
};
